
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import UploadModule, { ExtendedUploadStatus, UploadState, UploadStatus } from '@/store/modules/Upload';
import PhotosModule from '@/store/modules/Photos';
import { createInterfaceSound, playInterfaceSound } from '@/service/interfaceSound';

const uploadModule = getModule(UploadModule, store);
const photosModule = getModule(PhotosModule, store);

@Component({})
export default class StatusDrawer extends Vue {
    @Prop({
        required: true,
        type: Boolean,
    })
    public value!: boolean;
    public isOpen: boolean = false;
    public uploadSpeed: number = 0;
    private lastUploadedBytes: number = 0;
    private lastProgressAt: number = 0;

    playSlideSound() {
        const audio = createInterfaceSound('/sound/slide.wav');
        playInterfaceSound(audio).catch(error => {
            console.log('Audio playback failed:', error);
        });
    }

    get items(): Array<{ photoId?: string; file: File; status: ExtendedUploadStatus; title: string; order: number; icon: string; cssClass: string; smallThumb?: string, progressPercent: number }> {
        const mapped = this.uploadList
                .map(({ id, file, status, sessionId, progress }) => {
                    // Only load photo thumbnails for items that are actively uploading
                    // This prevents loading thumbnails for all session photos when modal opens
                    const photoSmallThumb = (id && (status === 'progress' || status === 'waiting')) ? 
                        photosModule.sessionPhotos[sessionId]?.find(
                            sessionPhoto => sessionPhoto.id === id
                        )?.smallThumbUrl : null;
                    const placeholderSmallThumb = uploadModule.placeholders[sessionId]?.find(
                        placeholder => placeholder.file === file
                    )?.smallThumbUrl;

                    const smallThumb = photoSmallThumb || placeholderSmallThumb;

                    return ({
                        file,
                        status,
                        title: file.name,
                        order:
                            status === 'error' ? 4 :
                            status === 'progress' ? 3 :
                            status === 'waiting' ? 2 :
                            status === 'finished' ? 1 : 0,
                        icon: status === 'waiting' ? 'schedule' :
                            status === 'progress' ? 'sync' :
                            status === 'finished' ? 'check' : 'error',
                        cssClass:
                            status === 'error' ? 'listError' :
                            status === 'waiting' ? 'listWaiting' :
                            status === 'progress' ? 'listProgress' :
                            status === 'finished' ? 'listFinished' : '',
                        ...(smallThumb ? { smallThumb } : {}),
                        progressPercent: progress.current * 100 / progress.overall
                    });
                })
        .sort(
          (item1, item2) => item1.order > item2.order ? -1 : 1
        );

        return mapped;
    }

    get uploadList() {
        return uploadModule.uploadList.map(({ id, file, status, sessionId, progress }) => ({ id, file, status, sessionId, progress }));
    }

    get progress() {
        return this.items.some(
            ({ status }) => status === 'waiting' || status === 'progress'
        );
    }

    get uploadedBytes() {
        return uploadModule.photoQueue.reduce(
            (sum, { progress }) => sum + progress.current,
            0,
        );
    }

    get uploadSpeedLabel() {
        const units = ['B/s', 'kB/s', 'MB/s', 'GB/s'];
        let speed = this.uploadSpeed;
        let unit = 0;

        while (speed >= 1024 && unit < units.length - 1) {
            speed /= 1024;
            unit++;
        }

        return `${speed >= 10 ? Math.round(speed) : speed.toFixed(1)} ${units[unit]}`;
    }

    get hasUploadErrors() {
        return this.items.some(
            ({ status }) => status === 'error'
        );
    }

    @Watch('uploadedBytes')
    public updateUploadSpeed(uploadedBytes: number) {
        const now = Date.now();
        if (!this.lastProgressAt || uploadedBytes < this.lastUploadedBytes) {
            this.uploadSpeed = 0;
        } else if (now > this.lastProgressAt) {
            this.uploadSpeed = (uploadedBytes - this.lastUploadedBytes) * 1000 / (now - this.lastProgressAt);
        }
        this.lastUploadedBytes = uploadedBytes;
        this.lastProgressAt = now;
    }

    @Watch('isOpen')
    public onToggle(value: boolean) {
        this.$emit('input', value);
        if (value) {
            this.playSlideSound();
        }
    }

    @Watch('value', {
        immediate: true,
    })
    public onValueChange(value: boolean) {
        this.isOpen = value;
    }

    public clearQueue() {
        uploadModule.clearFinishedUploadListElements();
    }

    public retryErrors() {
        uploadModule.retryAllFailed();
    }

    public cancel() {
        this.items.filter(({ status }) => status !== 'finished').forEach((item) => {
            uploadModule.cancelUpload({ file: item.file });
        })
    }
}
